import { comuneByIstat, gazetteer, withProvince } from '@sse/gazetteer';
import { normalizePlace } from '@sse/gazetteer/normalize';
import { Check, ExternalLink, MapPin, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ComunePicker } from '../components/comune-picker';
import { Card, Field, btnGhost, btnPrimary, inputCls } from '../components/ui';
import { ApiError } from '../lib/api';
import { formatRange, formatStamp } from '../lib/format';
import { useSession } from '../lib/session';

interface UnresolvedGroup {
  text: string;
  normalized: string;
  candidates: Array<{ id: string; title: string; startDate?: string; sourceId: string }>;
}
interface LearnedAlias {
  normalized: string;
  text: string;
  istat: string;
  comune: string;
  frazione?: string;
  createdAt: string;
  createdBy?: { email?: string };
}

const TABS = [
  ['unresolved', 'Da riconoscere'],
  ['aliases', 'Alias appresi'],
  ['gazetteer', 'Comuni e frazioni'],
] as const;
type Tab = (typeof TABS)[number][0];

const PROVINCE_NAMES: Record<string, string> = { LE: 'Lecce', BR: 'Brindisi', TA: 'Taranto' };

const osm = (lat: number, lng: number) =>
  `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=14/${lat}/${lng}`;

const errorText = (e: unknown) =>
  e instanceof ApiError
    ? ((e.body as { message?: string }).message ?? e.message)
    : e instanceof Error
      ? e.message
      : String(e);

/**
 * Luoghi (docs/07-admin-e-bot.md §2): i luoghi che l'import non ha riconosciuto si assegnano a
 * un comune (e frazione) una volta sola: diventano alias appresi, valgono subito per i
 * candidati in coda e per gli import successivi. In più l'elenco del gazetteer (comuni,
 * frazioni, alias di base, coordinate).
 */
export function Places() {
  const { api } = useSession();
  const [tab, setTab] = useState<Tab>('unresolved');
  const [groups, setGroups] = useState<UnresolvedGroup[] | null>(null);
  const [aliases, setAliases] = useState<LearnedAlias[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const [u, a] = await Promise.all([
        api<{ items: UnresolvedGroup[] }>('/places/unresolved'),
        api<{ items: LearnedAlias[] }>('/places/aliases'),
      ]);
      setGroups(u.items);
      setAliases(a.items);
    } catch (e) {
      setError(errorText(e));
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="mx-auto max-w-[1100px] p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-h2">Luoghi</h1>
        <button onClick={() => void load()} className={btnGhost}>
          <RefreshCw className="size-4" aria-hidden /> Aggiorna
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-1" role="tablist">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            onClick={() => setTab(k)}
            className={`h-9 px-3 text-sm ${tab === k ? 'bg-fg text-page' : 'bg-surface hover:bg-surface-strong'}`}
          >
            {label}
            {k === 'unresolved' && groups ? ` (${groups.length})` : ''}
            {k === 'aliases' && aliases ? ` (${aliases.length})` : ''}
          </button>
        ))}
      </div>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      <div className="mt-4">
        {tab === 'unresolved' &&
          (groups === null ? (
            <p className="text-sm text-muted">Caricamento…</p>
          ) : groups.length === 0 ? (
            <p className="text-sm text-muted">
              Tutti i luoghi delle segnalazioni in coda sono riconosciuti.
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {groups.map((g) => (
                <UnresolvedCard
                  key={g.normalized}
                  group={g}
                  onSaved={(alias) => {
                    setGroups((list) => list?.filter((x) => x.normalized !== g.normalized) ?? null);
                    setAliases((list) => [
                      ...(list ?? []).filter((a) => a.normalized !== alias.normalized),
                      alias,
                    ]);
                  }}
                />
              ))}
            </ul>
          ))}
        {tab === 'aliases' && (
          <AliasList
            aliases={aliases}
            onDeleted={(n) => setAliases((l) => l?.filter((a) => a.normalized !== n) ?? null)}
          />
        )}
        {tab === 'gazetteer' && <GazetteerBrowser aliases={aliases ?? []} />}
      </div>
    </main>
  );
}

function UnresolvedCard({
  group,
  onSaved,
}: {
  group: UnresolvedGroup;
  onSaved: (a: LearnedAlias) => void;
}) {
  const { api } = useSession();
  const [istat, setIstat] = useState<string | undefined>();
  const [frazione, setFrazione] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const comune = istat ? comuneByIstat(istat) : undefined;

  const save = async () => {
    if (!istat) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ alias: LearnedAlias; applied: number }>('/places/aliases', {
        method: 'POST',
        body: { text: group.text, istat, ...(frazione ? { frazione } : {}) },
      });
      onSaved(r.alias);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex flex-col gap-3 border border-surface-strong p-4">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <MapPin className="size-4 self-center" aria-hidden />
        <strong className="font-medium">«{group.text}»</strong>
        <span className="text-sm text-muted">
          {group.candidates.length === 1
            ? '1 evento in coda'
            : `${group.candidates.length} eventi in coda`}
        </span>
      </div>
      <ul className="flex flex-col gap-0.5 text-sm">
        {group.candidates.slice(0, 5).map((c) => (
          <li key={c.id}>
            <Link to={`/review/${c.id}`} className="hover:underline">
              {c.title}
            </Link>
            <span className="text-muted">
              {c.startDate ? ` · ${formatRange(c.startDate, c.startDate)}` : ''} · {c.sourceId}
            </span>
          </li>
        ))}
        {group.candidates.length > 5 && (
          <li className="text-muted">e altri {group.candidates.length - 5}</li>
        )}
      </ul>
      <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
        <Field label="Comune">
          <ComunePicker
            value={istat}
            onChange={(v) => {
              setIstat(v);
              setFrazione('');
            }}
          />
        </Field>
        <Field label="Frazione o località" hint="Facoltativa: coordinate più precise">
          <select
            value={frazione}
            onChange={(e) => setFrazione(e.target.value)}
            disabled={!comune?.frazioni.length}
            className={inputCls}
          >
            <option value="">{comune ? 'Il comune' : '—'}</option>
            {comune?.frazioni.map((f) => (
              <option key={f.slug} value={f.name}>
                {f.name}
              </option>
            ))}
          </select>
        </Field>
        <button disabled={!istat || busy} onClick={() => void save()} className={btnPrimary}>
          <Check className="size-4" aria-hidden /> Assegna
        </button>
      </div>
      <p className="text-xs text-muted">
        Da adesso «{group.text}» vale{' '}
        {comune
          ? `${frazione ? `${frazione}, ` : ''}${withProvince(comune.name, comune.province)}`
          : '…'}{' '}
        anche per i prossimi import.
      </p>
      {error && (
        <p role="alert" className="bg-danger-soft p-2 text-sm">
          {error}
        </p>
      )}
    </li>
  );
}

function AliasList({
  aliases,
  onDeleted,
}: {
  aliases: LearnedAlias[] | null;
  onDeleted: (normalized: string) => void;
}) {
  const { api } = useSession();
  const [error, setError] = useState<string | null>(null);
  if (aliases === null) return <p className="text-sm text-muted">Caricamento…</p>;
  if (!aliases.length)
    return (
      <p className="text-sm text-muted">
        Nessun alias appreso: si creano assegnando i luoghi da riconoscere.
      </p>
    );
  const remove = async (a: LearnedAlias) => {
    if (!window.confirm(`Togliere l’alias «${a.text}» → ${a.comune}?`)) return;
    try {
      await api(`/places/aliases/${encodeURIComponent(a.normalized)}`, { method: 'DELETE' });
      onDeleted(a.normalized);
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <>
      {error && (
        <p role="alert" className="mb-3 bg-danger-soft p-2 text-sm">
          {error}
        </p>
      )}
      <table className="w-full text-sm">
        <thead className="text-left text-[11px] uppercase tracking-[0.04em] text-muted">
          <tr>
            <th className="py-2 pr-3 font-medium">Testo</th>
            <th className="py-2 pr-3 font-medium">Luogo</th>
            <th className="py-2 pr-3 font-medium">Aggiunto</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {aliases.map((a) => (
            <tr key={a.normalized} className="border-t border-surface-strong">
              <td className="py-2 pr-3">{a.text}</td>
              <td className="py-2 pr-3">
                {a.frazione ? `${a.frazione}, ` : ''}
                {a.comune}
              </td>
              <td className="py-2 pr-3 text-muted">
                {formatStamp(a.createdAt)}
                {a.createdBy?.email ? ` · ${a.createdBy.email}` : ''}
              </td>
              <td className="py-2 text-right">
                <button onClick={() => void remove(a)} className={btnGhost}>
                  <Trash2 className="size-4" aria-hidden /> Togli
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function GazetteerBrowser({ aliases }: { aliases: LearnedAlias[] }) {
  const [q, setQ] = useState('');
  const learnedBy = useMemo(() => {
    const m = new Map<string, LearnedAlias[]>();
    for (const a of aliases) m.set(a.istat, [...(m.get(a.istat) ?? []), a]);
    return m;
  }, [aliases]);
  const list = useMemo(() => {
    const t = normalizePlace(q);
    return gazetteer.comuni.filter(
      (c) =>
        !t ||
        normalizePlace(c.name).includes(t) ||
        c.frazioni.some((f) => normalizePlace(f.name).includes(t)) ||
        c.aliases.some((a) => a.includes(t)),
    );
  }, [q]);
  const byProvince = (['LE', 'BR', 'TA'] as const)
    .map((p) => [p, list.filter((c) => c.province === p)] as const)
    .filter(([, l]) => l.length);

  return (
    <div className="flex flex-col gap-4">
      <label className="relative block max-w-[480px]">
        <Search className="absolute left-2 top-3 size-4 text-muted" aria-hidden />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca un comune, una frazione o un alias…"
          className={`${inputCls} pl-8`}
        />
      </label>
      <p className="text-xs text-muted">
        {gazetteer.comuni.length} comuni (dati ISTAT e OpenStreetMap, versione {gazetteer.version}).
        Comuni e frazioni nuovi si aggiungono nel file di configurazione del gazetteer; per un testo
        non riconosciuto basta un alias.
      </p>
      {byProvince.map(([p, comuni]) => (
        <Card key={p} title={`Provincia di ${PROVINCE_NAMES[p]} (${comuni.length})`}>
          <ul className="flex flex-col divide-y divide-surface-strong">
            {comuni.map((c) => (
              <li key={c.istat} className="flex flex-col gap-1 py-2 text-sm">
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <strong className="font-medium">{withProvince(c.name, c.province)}</strong>
                  <span className="text-xs text-muted">ISTAT {c.istat}</span>
                  <a
                    href={osm(c.centroid.lat, c.centroid.lng)}
                    target="_blank"
                    rel="noopener"
                    className="inline-flex items-center gap-1 text-xs text-muted hover:underline"
                  >
                    {c.centroid.lat.toFixed(4)}, {c.centroid.lng.toFixed(4)}
                    <ExternalLink className="size-3" aria-hidden />
                  </a>
                </div>
                {c.frazioni.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {c.frazioni.map((f) =>
                      f.lat !== undefined && f.lng !== undefined ? (
                        <a
                          key={f.slug}
                          href={osm(f.lat, f.lng)}
                          target="_blank"
                          rel="noopener"
                          className="bg-surface px-1.5 py-0.5 text-xs hover:bg-surface-strong"
                          title="Apri sulla mappa"
                        >
                          {f.name}
                        </a>
                      ) : (
                        <span key={f.slug} className="bg-surface px-1.5 py-0.5 text-xs">
                          {f.name}
                        </span>
                      ),
                    )}
                  </div>
                )}
                {(learnedBy.get(c.istat)?.length ?? 0) > 0 && (
                  <p className="text-xs">
                    <span className="text-muted">Alias appresi: </span>
                    {learnedBy
                      .get(c.istat)!
                      .map((a) => (a.frazione ? `${a.text} → ${a.frazione}` : a.text))
                      .join(' · ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}
